import { useState } from "react";
import { copyText } from "../lib/clipboard";
import { getInitDataUnsafe } from "../lib/telegram";
import css from "./ErrorScreen.module.css";

/**
 * B.1.5 ERROR state — used for NO_ROLE (403), network failure, and any
 * other terminal auth/data error. No support handles (A8). User can
 * either retry or sign out to switch the dev-staff identity.
 */
interface ErrorScreenProps {
  /** Short human title. Default: "НЕТ ДОСТУПА". */
  title?: string;
  /** Stable error code shown under the title — e.g. "ERR · NO_ROLE · 403". */
  code?: string;
  /** Detail line in normal sentence case. */
  detail?: string;
  /** Retry handler. */
  onRetry?: () => void;
  /** Sign-out handler. */
  onSignOut?: () => void;
  /** 03.10: свой Telegram ID с копированием (экран «Нет доступа»). */
  tgId?: number | null;
}

export function ErrorScreen({
  title = "НЕТ ДОСТУПА",
  code = "ERR · UNKNOWN · 500",
  detail = "Что-то пошло не так. Попробуй ещё раз.",
  onRetry,
  onSignOut,
  tgId,
}: ErrorScreenProps) {
  const [copied, setCopied] = useState<boolean | null>(null);
  return (
    <main className={css.root} role="alert">
      <div className={css.frame}>
        <span className={`${css.corner} ${css.tl}`} aria-hidden="true" />
        <span className={`${css.corner} ${css.tr}`} aria-hidden="true" />
        <span className={`${css.corner} ${css.bl}`} aria-hidden="true" />
        <span className={`${css.corner} ${css.br}`} aria-hidden="true" />

        <svg
          className={css.glyph}
          width="56"
          height="56"
          viewBox="0 0 56 56"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinejoin="round"
        >
          <path d="M28 4 L52 48 L4 48 Z" />
          <path d="M28 22 L28 36" strokeLinecap="round" />
          <circle cx="28" cy="42" r="1.5" fill="currentColor" />
        </svg>

        <h1 className={css.title}>{title}</h1>
        <p className={css.code}>{code}</p>
        <p className={css.detail}>{detail}</p>
        {tgId ? (
          <button type="button" className={css.tgId} onClick={() => void copyText(String(tgId)).then(setCopied)} title="Скопировать">
            <span className={css.tgIdLbl}>{copied === null ? "Ваш Telegram ID" : copied ? "Скопировано" : "Не скопировалось"}</span>
            <span className={css.tgIdNum}>{tgId}</span>
          </button>
        ) : null}

        <div className={css.actions}>
          {onRetry ? (
            <button type="button" className={css.btnPrimary} onClick={onRetry}>
              ПОВТОРИТЬ
            </button>
          ) : null}
          {onSignOut ? (
            <button type="button" className={css.btnGhost} onClick={onSignOut}>
              выйти из аккаунта
            </button>
          ) : null}
        </div>
      </div>
    </main>
  );
}

/**
 * 03.10 (владелец, кадры): /api/me ответил 403 — этот Telegram не привязан к работающему
 * сотруднику (не нанят, уволен или ссылка-приглашение ещё не открыта). Вместо
 * «НЕТ ПОДКЛЮЧЕНИЯ» с подсказками разработчику — «Нет доступа» и свой Telegram ID
 * с копированием: ИО может привязать его вручную.
 */
export function NoAccessScreen({ onRetry, onSignOut }: { onRetry?: () => void; onSignOut?: () => void }) {
  const tgId = getInitDataUnsafe().user?.id ?? null;
  return (
    <ErrorScreen
      title="НЕТ ДОСТУПА"
      code="ERR · AUTH · 403"
      detail={tgId
        ? "Этот Telegram не привязан к сотруднику. Вход открывает ссылка-приглашение от ОО или EO; для привязки вручную нужен ваш Telegram ID."
        : "Этот аккаунт не привязан к сотруднику. Вход открывает ссылка-приглашение от ОО или EO."}
      tgId={tgId}
      onRetry={onRetry}
      onSignOut={onSignOut}
    />
  );
}
